import React from 'react';

const ArbitrageResults = ({ selectedBets, arbitrageResults }) => {
  if (!selectedBets || selectedBets.length === 0) {
    return null;
  }

  if (!arbitrageResults) {
    return (
      <div style={{
        marginTop: '20px',
        padding: '15px',
        backgroundColor: '#f8f9fa',
        borderRadius: '6px',
        border: '1px solid #dee2e6'
      }}>
        <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>
          Arbitrage Analysis
        </h4>
        <p style={{ margin: 0, color: '#6c757d' }}>
          Select at least 2 betting options to calculate arbitrage opportunities.
        </p>
      </div>
    );
  }

  const americanToImpliedProb = (odds) => {
    const oddsNum = parseInt(odds);
    if (isNaN(oddsNum)) return null;
    
    if (oddsNum > 0) {
      return 100 / (oddsNum + 100);
    } else {
      return Math.abs(oddsNum) / (Math.abs(oddsNum) + 100);
    }
  };

  const calculateCombinationArbitrage = (bets) => {
    if (bets.length < 2) return null;

    const results = [];
    
    // Calculate arbitrage for all possible pairs
    for (let i = 0; i < bets.length; i++) {
      for (let j = i + 1; j < bets.length; j++) {
        const bet1 = bets[i];
        const bet2 = bets[j];
        
        const prob1 = americanToImpliedProb(bet1.odds);
        const prob2 = americanToImpliedProb(bet2.odds);
        
        if (prob1 && prob2) {
          const totalProb = prob1 + prob2;
          const arbitrageProfit = (1 - totalProb) * 100;
          
          results.push({
            bet1,
            bet2,
            totalImpliedProb: totalProb * 100,
            arbitrageProfit,
            hasArbitrage: arbitrageProfit > 0,
            prob1: prob1 * 100,
            prob2: prob2 * 100
          });
        }
      }
    }

    return results.sort((a, b) => b.arbitrageProfit - a.arbitrageProfit);
  };

  const combinations = calculateCombinationArbitrage(selectedBets);
  const bestArbitrage = combinations && combinations.length > 0 ? combinations[0] : null;

  return (
    <div style={{
      marginTop: '20px',
      padding: '15px',
      backgroundColor: 'white',
      borderRadius: '6px',
      border: '1px solid #dee2e6'
    }}>
      <h4 style={{ 
        margin: '0 0 15px 0', 
        color: '#333',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        🎯 Arbitrage Analysis Results
        <span style={{
          backgroundColor: selectedBets.length >= 2 ? '#28a745' : '#6c757d',
          color: 'white',
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '0.7em'
        }}>
          {selectedBets.length} bets selected
        </span>
      </h4>

      {!combinations || combinations.length === 0 ? (
        <div style={{
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          color: '#6c757d'
        }}>
          Not enough valid betting combinations to calculate arbitrage.
        </div>
      ) : (
        <>
          {/* Best Arbitrage Opportunity */}
          {bestArbitrage && (
            <div style={{
              marginBottom: '20px',
              padding: '15px',
              backgroundColor: bestArbitrage.hasArbitrage ? '#d4edda' : '#f8d7da',
              borderRadius: '6px',
              border: `1px solid ${bestArbitrage.hasArbitrage ? '#c3e6cb' : '#f5c6cb'}`
            }}>
              <h5 style={{ 
                margin: '0 0 10px 0', 
                color: bestArbitrage.hasArbitrage ? '#155724' : '#721c24'
              }}>
                {bestArbitrage.hasArbitrage ? '✅ Best Arbitrage Opportunity' : '❌ No Arbitrage Found'}
              </h5>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '10px' }}>
                <div style={{
                  padding: '10px',
                  backgroundColor: 'rgba(0, 123, 255, 0.1)',
                  borderRadius: '4px',
                  border: '1px solid rgba(0, 123, 255, 0.2)'
                }}>
                  <div style={{ fontWeight: 'bold', color: '#007bff', fontSize: '0.8em' }}>
                    {bestArbitrage.bet1.platform}
                  </div>
                  <div style={{ fontSize: '0.9em', fontWeight: 'bold', margin: '5px 0' }}>
                    {bestArbitrage.bet1.label}
                  </div>
                  <div style={{ fontSize: '1.1em', fontWeight: 'bold', color: '#007bff' }}>
                    {bestArbitrage.bet1.odds}
                  </div>
                  <div style={{ fontSize: '0.8em', color: '#6c757d' }}>
                    Implied Prob: {bestArbitrage.prob1.toFixed(1)}%
                  </div>
                </div>
                
                <div style={{
                  padding: '10px',
                  backgroundColor: 'rgba(108, 117, 125, 0.1)',
                  borderRadius: '4px',
                  border: '1px solid rgba(108, 117, 125, 0.2)'
                }}>
                  <div style={{ fontWeight: 'bold', color: '#6c757d', fontSize: '0.8em' }}>
                    {bestArbitrage.bet2.platform}
                  </div>
                  <div style={{ fontSize: '0.9em', fontWeight: 'bold', margin: '5px 0' }}>
                    {bestArbitrage.bet2.label}
                  </div>
                  <div style={{ fontSize: '1.1em', fontWeight: 'bold', color: '#6c757d' }}>
                    {bestArbitrage.bet2.odds}
                  </div>
                  <div style={{ fontSize: '0.8em', color: '#6c757d' }}>
                    Implied Prob: {bestArbitrage.prob2.toFixed(1)}%
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontWeight: 'bold', fontSize: '0.9em' }}>
                    Total Implied Probability: {bestArbitrage.totalImpliedProb.toFixed(1)}%
                  </span>
                </div>
                <div style={{
                  fontSize: '1.2em',
                  fontWeight: 'bold',
                  color: bestArbitrage.hasArbitrage ? '#155724' : '#721c24'
                }}>
                  {bestArbitrage.hasArbitrage ? 
                    `+${bestArbitrage.arbitrageProfit.toFixed(2)}% Profit` : 
                    `${bestArbitrage.arbitrageProfit.toFixed(2)}% Loss`
                  }
                </div>
              </div>
            </div>
          )}

          {/* All Combinations Table */}
          {combinations.length > 1 && (
            <div>
              <h5 style={{ margin: '0 0 10px 0', color: '#333' }}>
                All Possible Combinations ({combinations.length})
              </h5>
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.8em'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'left' }}>
                        Bet 1
                      </th>
                      <th style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'left' }}>
                        Bet 2
                      </th>
                      <th style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                        Total Prob
                      </th>
                      <th style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                        Arbitrage
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {combinations.map((combo, index) => (
                      <tr key={index} style={{
                        backgroundColor: combo.hasArbitrage ? '#d4edda' : 
                                       combo.arbitrageProfit > -2 ? '#fff3cd' : '#f8d7da'
                      }}>
                        <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                          <div style={{ fontWeight: 'bold' }}>
                            {combo.bet1.platform}: {combo.bet1.odds}
                          </div>
                          <div style={{ fontSize: '0.9em', color: '#6c757d' }}>
                            {combo.bet1.label}
                          </div>
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                          <div style={{ fontWeight: 'bold' }}>
                            {combo.bet2.platform}: {combo.bet2.odds}
                          </div>
                          <div style={{ fontSize: '0.9em', color: '#6c757d' }}>
                            {combo.bet2.label}
                          </div>
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                          {combo.totalImpliedProb.toFixed(1)}%
                        </td>
                        <td style={{ 
                          padding: '8px', 
                          border: '1px solid #dee2e6', 
                          textAlign: 'center',
                          fontWeight: 'bold',
                          color: combo.hasArbitrage ? '#155724' : '#721c24'
                        }}>
                          {combo.hasArbitrage ? '+' : ''}{combo.arbitrageProfit.toFixed(2)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ArbitrageResults;
